import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { getLocale } from '@ynap/cart-state'
import { getTimeout, getWatsonConfig, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { FAST_CHECKOUT_REQUEST, FAST_CHECKOUT_SUCCESS } from '../constants/ActionTypes'
import { getCurrentPaymentOption, getCurrentBillingAddress } from '../selectors/getPaymentStep'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { checkRectificationOnSuccess } from './cartRectification'
import { getCheckoutAppname, submitReduxForm } from './checkout'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'
import { formName as dduFormName } from 'app/components/DDUAcceptanceForm/DDUAcceptanceForm'
import getDuties from '../selectors/getDuties'
import { delay } from '@ynap/promise-utils'
import { raygun } from '@ynap/clientside-monitoring'
import { setPaymentMethodSuccess, setPaymentMethodError } from './setPaymentMethod'
import { collectBrowserInfo } from '../../../adyen'
import { setCheckoutNotification } from './checkoutNotification'

function fastCheckoutRequest() {
    return {
        type: FAST_CHECKOUT_REQUEST,
    }
}

function fastCheckoutSuccess(payload) {
    return {
        type: FAST_CHECKOUT_SUCCESS,
        payload,
    }
}


export function initialApplePayFlow(values) {
    return async (dispatch, getState, fetch) => {
        const state = getState()
        const { messages } = state
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const watsonConfig = getWatsonConfig(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            'x-origin': window.location.origin,
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state)
        }
        const appNameHeaderValue = getCheckoutAppname(state)
        const accessProfile = 'YNAP_PAYMENT_PSD2'
        const { deliveryDuty } = getDuties(state)
        const paymentRedirects = {
            x_returnUrl: '',
            x_cancelUrl: '',
        }
        const { returnUrl, isApplePay = false, applePayValidationUrl, userStatus } = values
        let dduAcceptance = {}
        if (deliveryDuty === 'DDU') {
            if (submitReduxForm(dduFormName, dispatch, state)) {
                dduAcceptance = { x_dduAcceptance: 'true' }
            }
        }
        let x_beaconSessionId
        if (state.config.head?.riskified?.enabled) {
            x_beaconSessionId = window?.sessionStorage.getItem('riskifiedSessionId') || undefined
        }
        dispatch(fastCheckoutRequest())
        const customRedirect = () => new ResponseError({ status: 401 })

        try {
            const json = await fetchJSON(Cart.applyCheckoutProfile({ ...watsonConfig, langId, additionalHeaders }), { customRedirect })
            const nextState = { ...state, cart: json.shoppingCart }
            const currentPaymentOption = getCurrentPaymentOption(nextState)
            
            
            if (currentPaymentOption && currentPaymentOption.id === 'APPLEPAY' || isApplePay) {
                await fetchJSON(
                    Cart.setPaymentInstruction({
                        paymentInstruction: [
                            {
                                'params.1.name': 'validationUrl',
                                'params.1.value': applePayValidationUrl,
                                payMethodId: 'APPLEPAY',
                                billing_address_id: '',
                                returnUrl,
                                cancelUrl: returnUrl,
                                paymentFromCart: 'true'
                            },
                        ],
                        ...watsonConfig,
                        langId,
                        additionalHeaders,
                    })
                )
              
                const json = await fetchJSON(Cart.checkout({
                    ...watsonConfig,
                    appNameHeaderValue,
                    accessProfile,
                    langId: langId,
                    body: {
                        ...paymentRedirects,
                        ...dduAcceptance,
                        x_beaconSessionId,
                    },
                    additionalHeaders
                }))

                if (json.isRedirect || json.redirectParameters) {
                    const { redirectMethod, redirectUrl, redirectType, token, paymentSession, ...redirectParams } = json.redirectParameters
                    ? json.redirectParameters[0]
                    : json

                    window.applePayToken = paymentSession
                    return { redirectType }
                }
            }
            
            
            dispatch(fastCheckoutSuccess(json))
            checkRectificationOnSuccess(dispatch, json)
            
        } catch (error) {
            if(error && error.response) {
                error.response.json().then((json) => {
                   if (json.errors[0]?.errorMessage === 'ERR_SHIPPING_LOCATION_INVALID') {
                        dispatch(setCheckoutNotification('error',messages?.errors?.apAddress(), { autoHideDelay: 5000 }))
                   } else {
                        dispatch(setCheckoutNotification('error', messages?.errors?.global(), { autoHideDelay: 5000 }))
                   }
    
                })
            } else {
                dispatch(setCheckoutNotification('error', messages?.errors?.global(), { autoHideDelay: 5000 }))
            }
            raygun.helpers.agent('send', {
                error,
                tags: ['initial flow', 'apple pay'],
            })
        }
    }
}

export function finaleApplePayFlow(params, addressId) {
    return async (dispatch, getState, fetch) => {
        const state = getState()
        const { messages } = state
        const watsonConfig = getWatsonConfig(state)
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const currentBillingAddress = getCurrentBillingAddress(state)
        const currentPaymentOption = getCurrentPaymentOption(state)

        if (!currentBillingAddress || !currentPaymentOption) {
            const error = new Error('Missing required currentBillingAddress or currentPaymentOption')
            raygun.helpers.agent('send', {
                error,
                tags: ['Apple Pay Authorise', 'updatePaymentMethod'],
                customData: {
                    orderId: state?.cart?.orderId,
                    buyerId: state?.cart?.x_buyerId,
                },
            })
        }

        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state)
        }
        const [paymentMethodId] = currentPaymentOption?.id?.split('/') || []
        const getPaymentTokens = () => {
            if (params.id === 'APPLEPAY') {
                return {
                    payMethodId: params.id,
                    'params.1.name': 'validationUrl',
                    'params.1.value': params.validationUrl,
                }
            }
            if (params.id === 'APPLEPAYUPDATE') {
                return {
                    payMethodId: 'APPLEPAY',
                    apiToken: params.token,
                    cc_lastFourDigits: params.lastFourDigits,
                }
            }
        }

        const browserInfo = collectBrowserInfo()
        const promise = fetchJSON(
            Cart.setPaymentInstruction({
                paymentInstruction: [
                    {
                        payMethodId: paymentMethodId,
                        billing_address_id: addressId,
                        ...getPaymentTokens(),
                        ...browserInfo,
                    },
                ],
                clearExisting: 'false',
                ...watsonConfig,
                langId,
                additionalHeaders,
            })
        )

        return delay(promise, getDelay(state))
        .then((data) => {
                dispatch(setPaymentMethodSuccess(data))

                return data
            })
            .catch((error) => {
                if(error.response) {
                    dispatch(setCheckoutNotification('error', messages?.errors?.global(), { autoHideDelay: 5000 }))
                }
                dispatch(setPaymentMethodError(error))
                throw error
            })
    }
}
